import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCents, todayISO } from "@/lib/format";
import { toast } from "sonner";

export function MarkPaidDialog({ record, open, onOpenChange, onDone }) {
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { if (open) { setDate(todayISO()); setSuccess(false); } }, [open]);

  if (!record) return null;
  const isInvoice = record.type === "invoice";
  const verb = isInvoice ? "paid" : "received";

  const confirm = async () => {
    setSaving(true);
    try {
      const base = isInvoice ? "invoices" : "payouts";
      const action = isInvoice ? "mark-paid" : "mark-received";
      await api.post(`/${base}/${record.id}/${action}`, { payment_date: date });
      setSuccess(true);
      setTimeout(() => { onOpenChange(false); onDone && onDone(); }, 900);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-paid-dialog">
        {success ? (
          <div className="py-8 text-center" data-testid="mark-paid-success">
            <span className="inline-grid place-items-center w-16 h-16 rounded-full bg-[#E7F6EF] text-status-success mx-auto animate-pop-in">
              <CheckCircle2 size={34} strokeWidth={2.4} />
            </span>
            <p className="font-heading font-bold text-lg mt-4 text-midnight">Marked {verb}!</p>
            <p className="text-sm text-ink-secondary mt-1">{formatCents(record.amount_cents, record.currency)} from {record.name}</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Mark {formatCents(record.amount_cents, record.currency)} from {record.name} as {verb}?</DialogTitle>
              <DialogDescription>{record.reference_code} · This records the {verb} date and logs an account event.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 py-2">
              <Label htmlFor="payment-date">{isInvoice ? "Payment" : "Received"} date</Label>
              <Input id="payment-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mark-paid-date" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button onClick={confirm} disabled={saving} data-testid="mark-paid-confirm">
                {saving ? "Saving…" : `Mark ${verb}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
